import { z } from 'zod'
import { DossierId, InstantieId, PublicatieId } from './id.js'
import { Moment } from './moment.js'

/**
 * Een document in de content-addressed opslag (ontwerp 4.9, U25).
 *
 * Het bestand is het bestand: het ligt onder zijn SHA-256 in de objectopslag, met
 * een verwijzingsrij in Postgres. Een antwoord van een bijlageveld draagt de
 * verwijzing (`DocumentVerwijzing` in revisie.ts), een revisie bevriest die
 * verwijzing mee, en niets kopieert ooit een bestand (R-199). Vervangen is een
 * verwijzing toevoegen en de oude weghalen; het bestand blijft bestaan zolang een
 * revisie ernaar wijst (R-200).
 *
 * Wat hier niet staat is waar het object precies ligt. Die sleutel is een keuze
 * van de opslagdienst en geen deel van het contract: wie het document opvraagt
 * noemt de hash, niet een pad.
 */

/**
 * Een volledige SHA-256, met zijn algoritme ervoor.
 *
 * Op één plek, want de hash staat in twee vormen in het model: als identiteit van
 * een document en als verwijzing in een antwoord. Twee regexen voor hetzelfde
 * begrip lopen uiteen zodra er ooit een tweede algoritme bij komt.
 */
export const Sha256 = z
  .string()
  .regex(/^sha256:[0-9a-f]{64}$/, 'verwacht een volledige SHA-256')
export type Sha256 = z.infer<typeof Sha256>

/**
 * Het mediatype zoals de aanbieder het meestuurt.
 *
 * Hier geen vaste lijst: een document is elk bestand in de opslag. Een bijlage die
 * het processysteem aanlevert is dat niet: daarvoor geldt sinds 28 oktober 2026 de
 * vaste lijst `FILE_KINDS`, met een controle op de inhoud (`BijlageSoort`), en
 * binnen die lijst kiest de inrichter per veld (R-104). Wel de vorm, want een
 * mediatype met een spatie of een puntkomma erin komt uit een header die niet is
 * geschoond.
 */
export const Mediatype = z
  .string()
  .regex(
    /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/,
    'verwacht een mediatype als application/pdf, zonder parameters',
  )
export type Mediatype = z.infer<typeof Mediatype>

export const Document = z.strictObject({
  hash: Sha256,
  grootteBytes: z.number().int().nonnegative(),
  mediatype: Mediatype,
  aangemaaktOp: Moment,
})
export type Document = z.infer<typeof Document>

/**
 * De soort van een bijlage, zoals de controle op de inhoud haar bij het aanleveren vaststelde
 * (R-104, U25). Eén per bestand, want de uitkomst hangt alleen van de bytes af.
 *
 * `soort` is hier tekst en geen `FileKind`: een soort die ooit van de lijst gaat, blijft in de opslag
 * staan zoals ze werd vastgesteld, en wie het bestand als bijlage wil noemen toetst haar tegen de lijst
 * van dat moment (`applyCorrections`).
 */
export const BijlageSoort = z.strictObject({
  hash: Sha256,
  soort: z.string().min(1),
  gecontroleerdOp: Moment,
})
export type BijlageSoort = z.infer<typeof BijlageSoort>

/** Voor welk dossier het processysteem een bijlage aanleverde, en langs welke aanroeper (R-201). */
export const BijlageAanlevering = z.strictObject({
  hash: Sha256,
  dossierId: DossierId,
  aangeleverdDoor: z.string().min(1),
  aangeleverdOp: Moment,
})
export type BijlageAanlevering = z.infer<typeof BijlageAanlevering>

/**
 * Waarlangs een document aan een publicatie en een instantie is gekoppeld (U25, R-104, migratie 0039).
 *
 * `aanlevering`: de pod leverde het aan op de instantie. `aanbieding`: de pod bood het aan op
 * `POST /v1/documenten`, zonder instantie, en alleen zijn eigen publicatie leest het terug.
 * `instantie`: het stond in wat de koppelvlakdienst bij het aanmaken meegaf of wat de opslag eruit
 * afleidde (de standen van eerdere blokken en de bevroren kopieën van de voorganger, R-201).
 * `voorganger`: de opvolger erfde het van zijn voorganger. `migratie`: het stond er al voor deze tabel, en migratie 0039 legde de koppeling eenmalig.
 */
export const DocumentKoppelingBron = z.enum(['aanlevering', 'aanbieding', 'instantie', 'voorganger', 'migratie'])
export type DocumentKoppelingBron = z.infer<typeof DocumentKoppelingBron>

/**
 * Een document hoort bij de instanties waarvoor het binnenkwam (U25, R-104, besluit van 1 november 2026).
 *
 * Een pod leest alleen een document met een koppeling aan zijn eigen publicatie. De publicatie staat er
 * zelf in, en niet alleen de instantie: een aanbieding zonder instantie hoort bij geen instantie, en een instantie verhuist nooit naar een andere publicatie (R-99).
 */
export const DocumentKoppeling = z.strictObject({
  hash: Sha256,
  publicatieId: PublicatieId,
  /** De instantie, of null bij een aanbieding zonder instantie. */
  instantieId: InstantieId.nullable(),
  bron: DocumentKoppelingBron,
  gekoppeldOp: Moment,
})
export type DocumentKoppeling = z.infer<typeof DocumentKoppeling>
